import Head from "next/head";
import { useState, useContext } from "react";
import CommonHead from "../../builder/CommonHead";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { splitFullName } from "../../helpers/formatter";
import { pageMapping } from "../../builder/mapping/page";
import {
  buildUrlWithQueryParams,
  useQueryParams,
} from "../../dataAccess/QueryParams";
import { useNavigateSameTab } from "../../helpers/navigation";
import { signUp, SignUpData, updateUser } from "../../dataAccess/api/user";
import LockScreen from "../ui/LockScreen";
import SignUpForm, {
  SignUpAttribute,
  isSignUpAttribute,
} from "../ui/SignUpForm";
import { ServerError } from "../../dataAccess/ServerError";
import { FullHeightContainer } from "../ui/FullHeightContainer";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import { DEFAULT_SIGN_UP_VALUE } from "../../../theme/defaultConstants";
import { isTranslationKey, useTranslation } from "../../helpers/i18n";
import { JSONAPIServerError } from "../../dataAccess/JSONAPIServerError";

export const availableComponents = {};

export type AvailableComponents = typeof availableComponents;

export const availableChildComponents: (keyof typeof availableComponents)[] =
  [];

interface Settings {
  label?: string;
  sublabel?: string;
  buttonLabel?: string;
  orLabel?: string;
  fullNamePlaceholderText?: string;
  companyNamePlaceholderText?: string;
  emailAddressPlaceholderText?: string;
  passwordPlaceholderText?: string;
  confirmPasswordPlaceholderText?: string;
  skipButtonLabel?: string;
  existingAccountLabel?: string;
  subscribeNewsletterLabel?: string;
}

interface Props {
  settings: Settings;
}

const SignUp = ({ settings }: Props) => {
  const { t } = useTranslation();
  const { push: navigate } = useNavigateSameTab();
  const { configuration } = useConfiguration();
  const { invitation_token, invite } = useQueryParams();
  const [signUpErrors, setSignUpErrors] = useState<
    Map<SignUpAttribute, string>
  >(new Map());
  const [serverError, setServerError] = useState<string>();
  const { isEditor, messages, setCurrentMessage } = useContext(EditorCtx);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data: SignUpData) => {
    if (isEditor) {
      setCurrentMessage(messages.previewAlert);
      return;
    }
    setLoading(true);
    setSignUpErrors(new Map());
    setServerError(undefined);

    try {
      let redirectPath = configuration?.isOpenMarketplace
        ? pageMapping.PLP.path
        : pageMapping.VerifyEmail.path;
      let query = {};
      const { firstName, lastName } = splitFullName(data.fullName);
      data.firstName = firstName;
      data.lastName = lastName;
      if (invitation_token) {
        await updateUser(data, invitation_token).json();
        redirectPath = pageMapping.PLP.path;
      } else {
        if (invite) {
          await signUp({ ...data, code: invite }).json();
        } else {
          await signUp(data).json();
        }
        query = { email: data.email };
      }

      navigate(buildUrlWithQueryParams(redirectPath, query));
    } catch (error) {
      if (error instanceof JSONAPIServerError) {
        const errors = error.getAllErrors();
        const newSignUpErrors = new Map<SignUpAttribute, string>();
        errors.forEach((err) => {
          const attribute = err.getAttribute();
          const code = err.getCode();
          if (
            isSignUpAttribute(attribute) &&
            typeof code === "string" &&
            isTranslationKey(code)
          ) {
            newSignUpErrors.set(attribute, t(code));
          }
        });
        setSignUpErrors(newSignUpErrors);
      } else if (error instanceof ServerError) {
        setServerError(error.getMessage());
      } else if (error instanceof Error) {
        setServerError(error.message);
      } else {
        setServerError(t("information_generic_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FullHeightContainer>
      <CommonHead titleSufix={settings?.label || DEFAULT_SIGN_UP_VALUE} />
      <Head>
        <meta name="description" content={t("seo_sign_up_meta_description")} />
      </Head>
      <LockScreen>
        <SignUpForm
          loading={loading}
          onSubmit={onSubmit}
          signUpErrors={signUpErrors}
          serverError={serverError}
          isInvite={!!invitation_token}
          settings={settings}
        />
      </LockScreen>
    </FullHeightContainer>
  );
};

export default SignUp;
export type SignupSettings = Settings;
